import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './ReviewExamplesPage.css';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function normalizeDraftStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return ['draft', 'approved', 'rejected', 'published'].includes(normalized) ? normalized : 'draft';
}

export default function ReviewExamplesPage() {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [targetCount, setTargetCount] = useState(20);
  const [productTypesInput, setProductTypesInput] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedDraftIdsByQueue, setSelectedDraftIdsByQueue] = useState({});
  const [actingQueueId, setActingQueueId] = useState('');
  const [queueStatusFilter, setQueueStatusFilter] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptTemplate, setPromptTemplate] = useState('');
  const [reconciling, setReconciling] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [queueSummary, setQueueSummary] = useState({
    total: 0,
    counts: {
      selected: 0,
      under_review: 0,
      approved_pending_publish: 0,
      publishing: 0,
      published: 0,
      publish_failed: 0,
      generating: 0,
    },
  });

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (queueStatusFilter) params.set('queueStatus', queueStatusFilter);
      const response = await authedFetch(`/api/v1/review-examples/queue?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load review queue.');
      }
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems(nextItems);
      setSelectedDraftIdsByQueue((prev) => {
        const next = {};
        nextItems.forEach((item) => {
          const id = String(item?.id || '').trim();
          if (!id) return;
          next[id] = Array.isArray(prev[id]) ? prev[id] : [];
        });
        return next;
      });
    } catch (err) {
      setError(err.message || 'Failed to load review queue.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, queueStatusFilter]);

  const loadQueueSummary = useCallback(async () => {
    try {
      const response = await authedFetch('/api/v1/review-examples/queue-summary');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load queue summary.');
      }
      setQueueSummary({
        total: Number(data?.total || 0),
        counts: {
          selected: Number(data?.counts?.selected || 0),
          under_review: Number(data?.counts?.under_review || 0),
          approved_pending_publish: Number(data?.counts?.approved_pending_publish || 0),
          publishing: Number(data?.counts?.publishing || 0),
          published: Number(data?.counts?.published || 0),
          publish_failed: Number(data?.counts?.publish_failed || 0),
          generating: Number(data?.counts?.generating || 0),
        },
      });
    } catch (_) {
      // Keep page usable even if summary fails.
    }
  }, [authedFetch]);

  const loadDailyStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await authedFetch('/api/v1/review-examples/stats/daily?days=7&timeZone=Asia/Kolkata');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load daily stats.');
      }
      setDailyStats(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      setDailyStats([]);
      setError(err.message || 'Failed to load daily stats.');
    } finally {
      setStatsLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    loadQueueSummary();
  }, [loadQueueSummary]);

  useEffect(() => {
    if (!statsOpen) return;
    loadDailyStats();
  }, [statsOpen, loadDailyStats]);

  const loadPromptSettings = useCallback(async () => {
    setPromptLoading(true);
    setError('');
    try {
      const response = await authedFetch('/api/v1/review-examples/settings');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load prompt settings.');
      }
      setPromptTemplate(String(data?.settings?.promptTemplate || ''));
    } catch (err) {
      setError(err.message || 'Failed to load prompt settings.');
    } finally {
      setPromptLoading(false);
    }
  }, [authedFetch]);

  const handleOpenPrompt = useCallback(async () => {
    setPromptOpen((prev) => !prev);
    if (!promptOpen) {
      await loadPromptSettings();
    }
  }, [loadPromptSettings, promptOpen]);

  const handleSavePrompt = useCallback(async () => {
    setPromptSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/review-examples/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promptTemplate }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to save prompt settings.');
      }
      setMessage(data?.message || 'Prompt settings saved.');
    } catch (err) {
      setError(err.message || 'Failed to save prompt settings.');
    } finally {
      setPromptSaving(false);
    }
  }, [authedFetch, promptTemplate]);

  const handleSelectProducts = useCallback(async () => {
    setSelecting(true);
    setError('');
    setMessage('');
    try {
      const productTypes = String(productTypesInput || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      const response = await authedFetch('/api/v1/review-examples/select-products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetCount: Math.max(20, Math.min(50, Number(targetCount) || 20)),
          productTypes,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to select products.');
      }
      setMessage(data?.message || 'Products selected.');
      await loadQueue();
      await loadQueueSummary();
      if (statsOpen) await loadDailyStats();
    } catch (err) {
      setError(err.message || 'Failed to select products.');
    } finally {
      setSelecting(false);
    }
  }, [authedFetch, loadDailyStats, loadQueue, loadQueueSummary, productTypesInput, statsOpen, targetCount]);

  const handleReconcile = useCallback(async () => {
    setReconciling(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/review-examples/reconcile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDateKey: '2026-04-01',
          includeResults: false,
          limit: 150,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to reconcile Shopify review state.');
      }
      setMessage(data?.message || 'Reconciliation completed.');
      await loadQueue();
      await loadQueueSummary();
      if (statsOpen) await loadDailyStats();
    } catch (err) {
      setError(err.message || 'Failed to reconcile Shopify review state.');
    } finally {
      setReconciling(false);
    }
  }, [authedFetch, loadDailyStats, loadQueue, loadQueueSummary, statsOpen]);

  const handleExportJudgeMeCsv = useCallback(async () => {
    setExportingCsv(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/review-examples/export-judgeme-csv');
      if (!response.ok) {
        let errMessage = 'Failed to export Judge.me CSV.';
        try {
          const errData = await response.json();
          errMessage = errData?.message || errMessage;
        } catch (_) {}
        throw new Error(errMessage);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const todayStr = new Date().toISOString().slice(0, 10);
      a.download = `judgeme_reviews_export_${todayStr}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setMessage('Judge.me CSV exported successfully!');
    } catch (err) {
      setError(err.message || 'Failed to export Judge.me CSV.');
    } finally {
      setExportingCsv(false);
    }
  }, [authedFetch]);

  const callQueueAction = useCallback(async (queueId, action, body = null) => {
    setActingQueueId(queueId);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch(`/api/v1/review-examples/${queueId}/${action}`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `Failed to ${action}.`);
      }
      setMessage(data?.message || 'Action completed.');
      await loadQueue();
      await loadQueueSummary();
      if (statsOpen) await loadDailyStats();
    } catch (err) {
      setError(err.message || `Failed to ${action}.`);
    } finally {
      setActingQueueId('');
    }
  }, [authedFetch, loadDailyStats, loadQueue, loadQueueSummary, statsOpen]);

  const toggleDraftSelection = useCallback((queueId, draftId, checked) => {
    setSelectedDraftIdsByQueue((prev) => {
      const existing = new Set(Array.isArray(prev[queueId]) ? prev[queueId] : []);
      if (checked) existing.add(draftId);
      else existing.delete(draftId);
      return { ...prev, [queueId]: Array.from(existing) };
    });
  }, []);

  const counters = useMemo(() => {
    const out = { selected: 0, under_review: 0, approved_pending_publish: 0, publishing: 0, published: 0, publish_failed: 0 };
    items.forEach((item) => {
      const key = String(item?.queueStatus || '').trim().toLowerCase();
      if (Object.prototype.hasOwnProperty.call(out, key)) out[key] += 1;
    });
    return out;
  }, [items]);

  return (
    <div className="review-examples-page">
      <div className="review-examples-hero">
        <div>
          <h1>Review Examples Admin</h1>
          <p>Select products from weekly analytics, generate random drafts (3-8), manually approve, then scheduled queue publishes one product every 10 minutes to <code>feedback.review</code>.</p>
        </div>
        <div className="review-examples-hero-actions">
          <label>
            Daily Target
            <input
              type="number"
              min="20"
              max="50"
              value={targetCount}
              onChange={(e) => setTargetCount(e.target.value)}
            />
          </label>
          <label>
            Product Types
            <input
              type="text"
              placeholder="e.g. Kurti, Suit, Saree"
              value={productTypesInput}
              onChange={(e) => setProductTypesInput(e.target.value)}
            />
          </label>
          <button type="button" className="primary" onClick={handleSelectProducts} disabled={selecting || loading}>
            {selecting ? 'Selecting…' : 'Select Products'}
          </button>
          <button type="button" onClick={handleReconcile} disabled={reconciling || loading || selecting}>
            {reconciling ? 'Reconciling…' : 'Reconcile Shopify'}
          </button>
          <button type="button" onClick={handleOpenPrompt} disabled={selecting || loading}>
            {promptOpen ? 'Hide Prompt' : 'Prompt Settings'}
          </button>
          <button
            type="button"
            onClick={() => (statsOpen ? Promise.all([loadQueue(), loadDailyStats()]) : loadQueue())}
            disabled={loading || selecting || statsLoading}
          >
            Refresh
          </button>
          <button
            type="button"
            className="secondary"
            onClick={handleExportJudgeMeCsv}
            disabled={exportingCsv || loading || selecting}
          >
            {exportingCsv ? 'Exporting CSV…' : 'Download Judge.me CSV'}
          </button>
        </div>
      </div>

      {promptOpen ? (
        <div className="review-prompt-panel">
          <div className="review-prompt-header">
            <h2>Review Generation Prompt</h2>
            <button type="button" className="primary" onClick={handleSavePrompt} disabled={promptLoading || promptSaving}>
              {promptSaving ? 'Saving…' : 'Save Prompt'}
            </button>
          </div>
          <p>Edit the exact prompt used for draft generation.</p>
          <textarea
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            rows={18}
            disabled={promptLoading || promptSaving}
          />
        </div>
      ) : null}

      <div className="review-examples-toolbar">
        <div className="review-pills">
          <span className="pill selected">Selected {counters.selected}</span>
          <span className="pill review">Under Review {counters.under_review}</span>
          <span className="pill review">Pending Publish {counters.approved_pending_publish}</span>
          <span className="pill review">Publishing {counters.publishing}</span>
          <span className="pill published">Published {counters.published}</span>
          <span className="pill failed">Publish Failed {counters.publish_failed}</span>
        </div>
        <label>
          Filter
          <select value={queueStatusFilter} onChange={(e) => setQueueStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="selected">Selected</option>
            <option value="under_review">Under Review</option>
            <option value="approved_pending_publish">Approved Pending Publish</option>
            <option value="publishing">Publishing</option>
            <option value="published">Published</option>
            <option value="publish_failed">Publish Failed</option>
          </select>
        </label>
      </div>

      <section className="review-live-summary">
        <h2>Live Queue Status</h2>
        <div className="review-live-summary-grid">
          <div><strong>Total</strong><span>{queueSummary.total}</span></div>
          <div><strong>Generating</strong><span>{queueSummary.counts.generating}</span></div>
          <div><strong>Selected</strong><span>{queueSummary.counts.selected}</span></div>
          <div><strong>Under Review</strong><span>{queueSummary.counts.under_review}</span></div>
          <div><strong>Pending Publish</strong><span>{queueSummary.counts.approved_pending_publish}</span></div>
          <div><strong>Publishing</strong><span>{queueSummary.counts.publishing}</span></div>
          <div><strong>Published</strong><span>{queueSummary.counts.published}</span></div>
          <div><strong>Publish Failed</strong><span>{queueSummary.counts.publish_failed}</span></div>
        </div>
      </section>

      <section className="review-stats-panel">
        <div className="review-stats-panel-header">
          <h2>Last 7 Days (IST)</h2>
          <button
            type="button"
            className="review-stats-toggle"
            onClick={() => setStatsOpen((prev) => !prev)}
            disabled={statsLoading}
          >
            {statsOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        {statsOpen && statsLoading ? <div className="review-stats-loading">Loading daily stats…</div> : null}
        {statsOpen && !statsLoading && dailyStats.length === 0 ? <div className="review-stats-empty">No daily stats available.</div> : null}
        {statsOpen && !statsLoading && dailyStats.length > 0 ? (
          <div className="review-stats-table-wrap">
            <table className="review-stats-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Generated Products</th>
                  <th>Generated Reviews</th>
                  <th>Published Products</th>
                  <th>Published Reviews</th>
                </tr>
              </thead>
              <tbody>
                {dailyStats.map((row) => (
                  <tr key={String(row?.date || '')}>
                    <td>{row?.date || '—'}</td>
                    <td>{Number(row?.generatedProducts || 0)}</td>
                    <td>{Number(row?.generatedReviews || 0)}</td>
                    <td>{Number(row?.publishedProducts || 0)}</td>
                    <td>{Number(row?.publishedReviews || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {error ? <div className="review-alert error">{error}</div> : null}
      {message ? <div className="review-alert success">{message}</div> : null}

      {loading ? <div className="review-loading">Loading review queue…</div> : null}
      {!loading && items.length === 0 ? <div className="review-empty">No queue items found.</div> : null}

      <div className="review-queue-grid">
        {items.map((item) => {
          const queueId = String(item?.id || '').trim();
          const drafts = Array.isArray(item?.drafts) ? item.drafts : [];
          const selectedIds = new Set(Array.isArray(selectedDraftIdsByQueue[queueId]) ? selectedDraftIdsByQueue[queueId] : []);
          const approvableIds = drafts
            .map((draft) => ({ id: String(draft?.id || '').trim(), status: normalizeDraftStatus(draft?.status) }))
            .filter((draft) => draft.id && (draft.status === 'draft' || draft.status === 'rejected'))
            .map((draft) => draft.id);
          const rejectedCount = drafts.filter((draft) => normalizeDraftStatus(draft?.status) === 'rejected').length;
          const isBusy = actingQueueId === queueId;
          return (
            <article key={queueId} className="review-card">
              <div className="review-card-header">
                <div>
                  <h3>{item?.productTitle || `Product ${item?.productId || ''}`}</h3>
                  <p>
                    Type: <strong>{item?.productType || '—'}</strong> | SKU: <strong>{item?.primarySku || item?.sourceSku || '—'}</strong> | Fabric: <strong>{item?.fabric || '—'}</strong> | Quality: <strong>{item?.quality || '—'}</strong>
                  </p>
                  <p>
                    Status: <strong>{item?.queueStatus || 'selected'}</strong> | Retry: <strong>{Number(item?.retryCount || 0)}/2</strong> | Updated: <strong>{formatDateTime(item?.updatedAt)}</strong>
                  </p>
                  <p>
                    Verify: <strong>{item?.shopifyVerifyStatus || '—'}</strong> | Republish Attempts: <strong>{Number(item?.republishAttempts || 0)}</strong> | Last Verified: <strong>{formatDateTime(item?.shopifyVerifiedAt)}</strong>
                  </p>
                </div>
                {item?.imageUrls?.[0] ? <img src={item.imageUrls[0]} alt={item?.productTitle || 'Product'} /> : null}
              </div>

              <div className="review-card-actions">
                <button type="button" disabled={isBusy || drafts.length > 0} onClick={() => callQueueAction(queueId, 'generate')}>Generate</button>
                <button type="button" disabled={isBusy || Number(item?.retryCount || 0) >= 2} onClick={() => callQueueAction(queueId, 'regenerate')}>Regenerate</button>
                <button
                  type="button"
                  disabled={isBusy || Number(item?.retryCount || 0) >= 2 || rejectedCount === 0}
                  onClick={() => callQueueAction(queueId, 'regenerate-rejected')}
                >
                  Regenerate Only Rejected
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={isBusy || approvableIds.length === 0}
                  onClick={() => callQueueAction(queueId, 'approve', { ids: approvableIds })}
                >
                  Approve All
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={isBusy || selectedIds.size === 0}
                  onClick={() => callQueueAction(queueId, 'approve', { ids: Array.from(selectedIds) })}
                >
                  Approve Selected
                </button>
                <button
                  type="button"
                  disabled={isBusy || selectedIds.size === 0}
                  onClick={() => callQueueAction(queueId, 'reject', { ids: Array.from(selectedIds) })}
                >
                  Reject Selected
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={isBusy || (item?.queueStatus !== 'approved_pending_publish' && item?.queueStatus !== 'publish_failed')}
                  onClick={() => callQueueAction(queueId, 'publish')}
                >
                  Publish Now
                </button>
              </div>

              {item?.publishError ? <div className="review-alert error compact">{item.publishError}</div> : null}
              {Array.isArray(item?.shopifyMissingIds) && item.shopifyMissingIds.length > 0 ? (
                <div className="review-alert error compact">
                  Missing on Shopify metafield: {item.shopifyMissingIds.join(', ')}
                </div>
              ) : null}

              <div className="review-drafts">
                {drafts.length === 0 ? <div className="review-draft-empty">No drafts generated yet.</div> : null}
                {drafts.map((draft) => {
                  const draftId = String(draft?.id || '').trim();
                  const status = normalizeDraftStatus(draft?.status);
                  const checked = selectedIds.has(draftId);
                  return (
                    <label key={draftId} className={`review-draft status-${status}`}>
                      <div className="review-draft-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleDraftSelection(queueId, draftId, e.target.checked)}
                          disabled={isBusy || status === 'published'}
                        />
                        <span>{status}</span>
                      </div>
                      <p>{draft?.text || '—'}</p>
                      <div className="review-draft-meta">
                        <span>Rating: {Number(draft?.rating || 0).toFixed(1)}/5</span>
                        <span>Created: {formatDateTime(draft?.created_at)}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
